import { Route, ListChecks, TrendingUp, BookOpen, Quote, Sun, Flame, BarChart3, Trophy } from 'lucide-react';
import { Reveal, Chapter } from './Reveal';
import { GoldButton } from './GoldButton';
import { APP_URL } from '../../config/site';
import { trackClick } from '../../lib/track';

const FEATURES = [
  { icon: Route, label: 'Jornada de 28 dias' },
  { icon: ListChecks, label: 'Rastreador de hábitos' },
  { icon: TrendingUp, label: 'Sistema de evolução' },
  { icon: BookOpen, label: 'Biblioteca' },
  { icon: Quote, label: 'Frases motivacionais' },
  { icon: Sun, label: 'Versículos diários' },
  { icon: Flame, label: 'Desafios diários' },
  { icon: BarChart3, label: 'Progresso' },
  { icon: Trophy, label: 'Conquistas' },
];

export const AppSection = () => (
  <section data-testid="app-section" className="relative mx-auto max-w-6xl px-6 py-24 sm:px-10 sm:py-36">
    <div className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[500px] w-[700px] -translate-x-1/2 rounded-full bg-gold/5 blur-[140px]" />

    <Chapter index="01" label="O Aplicativo" />

    <div className="grid items-center gap-16 lg:grid-cols-2">
      <div>
        <Reveal>
          <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Sua jornada de 28 dias,
            <span className="text-gold"> no seu bolso.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-neutral-400">
            Um aplicativo web premium, direto no navegador do seu celular. Sem downloads complicados:
            você abre, registra o dia e evolui — todos os dias, por 28 dias.
          </p>
        </Reveal>

        <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {FEATURES.map((f, i) => (
            <Reveal key={f.label} delay={0.05 * i} y={18}>
              <div
                data-testid={`feature-chip-${i}`}
                className="group flex h-full flex-col gap-3 rounded-2xl border border-white/[0.07] bg-graphite p-4 transition-colors duration-300 hover:border-gold/40"
              >
                <f.icon className="h-5 w-5 text-gold/80 transition-colors duration-300 group-hover:text-gold-light" strokeWidth={1.8} />
                <span className="text-[13px] font-medium leading-snug text-neutral-300">{f.label}</span>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-12">
          <p className="font-display text-xl font-bold tracking-tight text-white sm:text-2xl">
            Comece sua transformação <span className="text-gold">gratuitamente.</span>
          </p>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-neutral-400">
            Dê o primeiro passo para desenvolver foco, disciplina e constância.
          </p>
          <GoldButton
            testId="app-cta-button"
            href={APP_URL}
            onClick={() => trackClick('app-acessar-gratuitamente', 'aplicativo')}
            className="mt-7"
          >
            Acessar gratuitamente
          </GoldButton>
        </Reveal>
      </div>

      <div className="relative mx-auto flex w-full max-w-md items-end justify-center gap-5">
        <Reveal delay={0.15} className="w-[46%] translate-y-8">
          <div className="relative aspect-[3/4] overflow-hidden rounded-[2rem] border border-white/10 bg-[#0a0a0a] shadow-[0_30px_80px_rgba(0,0,0,0.7)] transition-transform duration-500 hover:scale-[1.03]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(212,175,55,0.10),transparent_65%)]" />
            <div className="absolute left-1/2 top-3 h-4 w-16 -translate-x-1/2 rounded-full bg-black/80" />
            <div className="absolute inset-x-0 top-3 flex items-center justify-between px-5">
              <span className="font-accent text-[10px] font-medium text-neutral-400">09:41</span>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <img
                src="/assets/app-icon.webp"
                alt="Ícone do app Despertar da Águia na tela inicial"
                loading="lazy"
                className="w-[42%] rounded-[26%] shadow-[0_18px_45px_rgba(0,0,0,0.8),0_0_35px_rgba(212,175,55,0.15)]"
              />
              <span className="text-[10px] font-medium tracking-wide text-neutral-300">Despertar da Águia</span>
            </div>
            <div className="absolute inset-x-5 bottom-3 flex justify-center gap-3 rounded-2xl bg-white/[0.04] py-2.5 backdrop-blur-sm">
              {[0, 1, 2].map((d) => (
                <span key={d} className="h-7 w-7 rounded-lg bg-white/[0.06]" />
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.3} className="w-[54%]">
          <div className="animate-float-slow aspect-[3/4] overflow-hidden rounded-[2rem] border border-gold/20 shadow-[0_30px_90px_rgba(212,175,55,0.12)] transition-transform duration-500 hover:scale-[1.03]">
            <img src="/assets/mockup-app.webp" alt="Aplicativo aberto na Jornada de 28 dias" loading="lazy" className="h-full w-full object-cover object-center" />
          </div>
        </Reveal>
        <div className="pointer-events-none absolute -bottom-10 left-1/2 -z-10 h-40 w-[120%] -translate-x-1/2 rounded-full bg-gold/10 blur-[90px]" />
      </div>
    </div>
  </section>
);
